import color from 'color';
import i18n from '../../i18n';
import { getRandomNumber } from '../Number/NumberUtils';
import profanity from 'profanity-util';
import { PositionXY } from 'components/Storyboard/StoryboardEditor/FullscreenControls/types';
import { Widget } from '@sprinklr/stories/widget/Widget';

export const GenerateSafeString = (text: string, prefix?: string): string => {
    if (!text) {
        return '';
    }
    text = text + '';

    const safeString: string = stripBadChars(text);
    const safeStrings = stringToArray(safeString);
    const combinedSrings = safeStrings && safeStrings.join(' ');

    return (combinedSrings && stringReplace(combinedSrings, prefix)).trim() || '';
};

export const GenerateLabelClasses = (label: string, prefix): string => {
    if (!label) {
        return '';
    }

    let safeStringsArray;
    const safeString: string = stripBadChars(label.toString() + '');
    const safeStrings = stringToArray(safeString);

    if (safeStrings?.length) {
        safeStringsArray = safeStrings.map(
            (
                string,
                index // map over array add prefix to each member
            ) => prefix + '_tag_' + string.replace('-', '')
        );
    } else {
        safeStringsArray = [safeString];
    }

    return prefix + stringReplace(safeString, '_label') + safeStringsArray.join(' ');
};

/**
 * Strip bad characters and leave whitespace intact
 */
export const stripBadChars = (string: string): string => {
    if (!string) {
        return '';
    }
    return (string + '')
        .replace(/^@/, 'at_')
        .replace(/^#/, 'hash_')
        .replace(/[!"#$%&'()*+,.\/:;<=>?@\[\\\]^`{|}~]/g, '') // strip out bad characters
        .toLowerCase()
        .trim();
};

export const stringToArray = (string: string): string[] => {
    if (!string) {
        return [];
    }
    return (string + '').replace('_', '').match(/[^\s-]+-?/g);
};

export const stringReplace = (string: string, prefix?: string): string => {
    const prefixString = prefix ? prefix + '_' : '';
    return (
        prefixString +
        string
            .replace(/(\s+|-+|-_-|_-|-_)/g, '_') // underscore all the things
            .replace(/_+/g, '_') // condense duplicate underscores
            .replace(/_$/g, '') + // remove underscore if it's the last character
        (!string.length ? '_' : '') + // Special case when we have empty labels for BarChart: https://sprinklr.atlassian.net/browse/DISPLAY-1609
        ' '
    );
};

export const stripHtmlTags = (string: string) => {
    return string.replace(/(<([^>]+)>)/gi, '');
};

// extract string segment based on preceding and superseding strings
export const extractString = (string: string, before: string, after?: string): string => {
    const newString =
        string && string.includes(before) ? string.split(before)[1].split(after)[0] : '';
    return stripHtmlTags(newString);
};

export const convertColorEventToString = event => {
    if (event && event.rgb) {
        const rgba = [];
        for (const prop in event.rgb) {
            rgba.push(event.rgb[prop]);
        }
        return color(rgba).string();
    } else {
        return event;
    }
};

// extract string segment based on preceding and superseding strings
export const titleCase = (string: string): string => {
    const custom = string.split('π');
    const result = custom[custom.length - 1];
    return result.replace(/_/g, ' ').replace(/\w\S*/g, txt => {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
};

export const resolveSceneSlide = (isPres = false, lowercase = false, plural = false): string => {
    let output = i18n.t('Scene');

    if (isPres) {
        output = i18n.t('Slide');
    }

    if (plural) {
        output = `${output}s`;
    }
    return lowercase ? output.toLowerCase() : output;
};

export const resolveAppMode = (isPres = false, plural = false): string => {
    return isPres ? `Presentation${plural ? 's' : ''}` : `Storyboard${plural ? 's' : ''}`;
};

export const toSentenceCase = (raw: string): string => {
    const result = raw
        .replace(/_+/g, ' ')
        .replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
    return result;
};

export const getHipsum = (): string => {
    const raw = `Lorem ipsum dolor amet bitters ramps iceland sriracha literally lo-fi. Knausgaard cliche you probably haven't heard of them selvage fixie la croix man bun twee messenger bag bespoke letterpress portland bushwick whatever leggings. Pok pok hammock tbh lo-fi. Meh pickled mlkshk tacos, master cleanse tofu edison bulb. Twee kickstarter locavore ennui pinterest, gentrify kinfolk pickled affogato. Iceland direct trade ethical forage. Small batch stumptown live-edge chicharrones four dollar toast twee. Franzen normcore keytar jean shorts forage. Quinoa retro sartorial chartreuse yuccie. Mumblecore mixtape direct trade poke. Marfa hot chicken vexillologist pok pok sriracha ramps pickled jianbing everyday carry you probably haven't heard of them. Migas waistcoat pabst deep v meggings, hell of gluten-free live-edge hot chicken direct trade brunch. Jianbing yuccie green juice, food truck leggings pickled single-origin coffee etsy scenester put a bird on it venmo vegan. Aesthetic marfa williamsburg, tumblr tacos small batch portland humblebrag gluten-free poutine edison bulb freegan letterpress vaporware schlitz. Craft beer brooklyn shoreditch leggings keytar ethical. Glossier four loko PBR&B art party. Pabst tofu affogato, art party edison bulb austin fingerstache iceland echo park venmo disrupt wolf flannel taiyaki retro. Paleo adaptogen taiyaki, put a bird on it bicycle rights organic artisan lomo pork belly. Distillery art party PBR&B, put a bird on it master cleanse la croix ethical. Shabby chic wolf tumblr mixtape chicharrones polaroid gluten-free palo santo put a bird on it pour-over YOLO yr church-key ugh. 3 wolf moon thundercats copper mug, freegan letterpress kitsch raclette fingerstache. Yr four loko woke cred gastropub, wolf blue bottle XOXO coloring book kitsch franzen williamsburg ugh dreamcatcher. 8-bit cliche green juice raw denim farm-to-table normcore, copper mug slow-carb palo santo mumblecore XOXO hot chicken umami. Craft beer lumbersexual vape cardigan vexillologist palo santo affogato mlkshk enamel pin keffiyeh pitchfork tousled synth copper mug. Coloring book biodiesel messenger bag, keffiyeh vice tumeric pork belly stumptown sustainable before they sold out farm-to-table echo park wolf. Lo-fi fashion axe tote bag synth roof party typewriter squid. Literally single-origin coffee jean shorts iceland, mlkshk cronut knausgaard post-ironic schlitz cliche lo-fi fingerstache 3 wolf moon flexitarian. Cornhole sustainable messenger bag raw denim retro church-key next level godard stumptown. Before they sold out banjo marfa biodiesel next level normcore man braid distillery umami skateboard. Oh. You need a little dummy text for your mockup? How quaint. I bet you’re still using Bootstrap too…`;
    const start = getRandomNumber(0, 200);
    const stop = getRandomNumber(start, 200);
    return raw.slice(start, stop);
};

export const getHipsterName = (): string => {
    const names = [
        'Tiger',
        'Atticus',
        'Bruno',
        'August',
        'Ash',
        'Daisy',
        'Edie',
        'Breah',
        'India',
        'Jupiter',
        'Sia',
        'Wren',
        'Byron',
        'Fenton',
        'Django',
        'Hugo',
        'Jasper',
        'Nico',
        'Odin',
        'Sebastian',
        'Zeus',
        'Theo',
        'Cobi',
        'Bracken',
        'Mishka',
        'Teigen',
        'Pike',
        'Kai',
        'Hudson',
        'Juniper',
        'Ivy',
        'Brax',
        'Greer',
        'Holden',
        'Ignatius',
        'Kingston',
        'Miri',
        'Freya',
        'Ramona',
        'Zelda',
        'Chester',
        'Mabel',
        'Oscar',
        'Calliope',
        'Rhett',
        'Jinja',
        'Apollo',
        'Fox',
    ];
    return names[getRandomNumber(0, names.length - 1)];
};

export const queryParamKeywords = {
    true: true,
    false: false,
    null: null,
    undefined,
};

export function resolveQueryParam<T extends string | boolean | string[] | PositionXY>(
    param: string | string[],
    defaultValue: string
): T {
    // check if it's a boolean param
    if (defaultValue === 'false' || defaultValue === 'true') {
        return !!queryParamKeywords[(param as string) ?? `${defaultValue}`] as T;
    }
    return (param ?? `${defaultValue}`) as T;
}

export function purifyString(str: string, { obscureSymbol, profanityList }): string {
    const list = profanityList?.length > 0 ? profanityList : undefined;
    const [purified, matchedVals] = profanity.purify(str, {
        forbiddenList: list,
        obscureSymbol,
        substrings: 'lite',
    });

    return purified as string;
}

export function isValidColorString(string): boolean {
    const style = new Option().style;
    style.color = string;
    return style.color.length > 0;
}

export function isValidURL(string): boolean {
    const a = document.createElement('a');
    a.href = string;
    return a.host && a.host != window.location.host;
}

export function sanitizeListeningExplorerQueryString(inputString): string {
    // Strip non-alpha, non-space chars from user-provided query
    // Allow hashtag, underscore
    return inputString.replace(/[^a-z0-9\s#_]/gi, '');
}

export function getAssetUrlIdFromWidget(widget: Widget): string | null {
    if (widget?.options?.contentImageUrl?.includes('data.sprinklr.com')) {
        const url = new URL(widget.options.contentImageUrl);
        const id =
            url.pathname.includes('/userAsset/') &&
            (url.pathname?.replace(/.*\/userAsset\//, '') ?? null);
        return id || null;
    }
    return null;
}
